import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Users } from 'lucide-react';
import EmptyState from './illustrations/EmptyState';

import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function AdminDrivers() {
    const [drivers, setDrivers] = useState([]);
    const [showAddDriver, setShowAddDriver] = useState(false);
    const [newDriver, setNewDriver] = useState({ name: '', phone: '', whatsapp_number: '', password: '' });
    const [driverError, setDriverError] = useState('');
    const [editingDriverId, setEditingDriverId] = useState(null);
    const [editDriver, setEditDriver] = useState({ name: '', phone: '', whatsapp_number: '', active: true, password: '' });
    const navigate = useNavigate();

    const fetchDrivers = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const response = await axios.get(`${API_URL}/api/admin/drivers`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDrivers(response.data);
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                navigate('/login');
            }
        }
    };

    useEffect(() => {
        fetchDrivers();
        const interval = setInterval(fetchDrivers, 20000);
        return () => clearInterval(interval);
    }, []);

    const handleAddDriver = async (e) => {
        e.preventDefault();
        setDriverError('');
        const token = localStorage.getItem('token');
        try {
            await axios.post(`${API_URL}/api/admin/drivers`, newDriver, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewDriver({ name: '', phone: '', whatsapp_number: '', password: '' });
            setShowAddDriver(false);
            fetchDrivers();
        } catch (error) {
            setDriverError(error.response?.data?.error || 'Failed to add driver');
        }
    };

    const startEditDriver = (driver) => {
        setEditingDriverId(driver.id);
        setEditDriver({ name: driver.name, phone: driver.phone, whatsapp_number: driver.whatsapp_number, active: driver.active, password: '' });
    };

    const handleUpdateDriver = async (e, id) => {
        e.preventDefault();
        setDriverError('');
        const token = localStorage.getItem('token');
        try {
            const payload = { ...editDriver };
            if (!payload.password) delete payload.password;
            await axios.patch(`${API_URL}/api/admin/drivers/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEditingDriverId(null);
            fetchDrivers();
        } catch (error) {
            setDriverError(error.response?.data?.error || 'Failed to update driver');
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/login');
    };

    return (
        <div className="dashboard-page admin-compact">
            <div className="dashboard-container">
                <div className="dashboard-header">
                    <div>
                        <h1><Users size={26} className="page-icon" /> Drivers</h1>
                        <p>Manage driver accounts</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={handleLogout} className="logout-btn">Logout</button>
                    </div>
                </div>

                <div className="filters" style={{ marginBottom: '24px' }}>
                    <Link to="/admin/dashboard" style={{ textDecoration: 'none' }}>Bookings</Link>
                    <Link to="/admin/drivers" className="active" style={{ textDecoration: 'none' }}>Drivers</Link>
                    <Link to="/admin/calendar" style={{ textDecoration: 'none' }}>Calendar</Link>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ color: 'var(--dark)' }}>Drivers ({drivers.length})</h2>
                    <button className="refresh-btn" onClick={() => setShowAddDriver(!showAddDriver)}>
                        {showAddDriver ? 'Cancel' : '+ Add Driver'}
                    </button>
                </div>

                {showAddDriver && (
                    <form onSubmit={handleAddDriver} className="booking-card" style={{ marginBottom: '16px' }}>
                        {driverError && <div className="error-message" style={{ marginBottom: '12px' }}>{driverError}</div>}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
                            <input
                                type="text"
                                placeholder="Driver name"
                                value={newDriver.name}
                                onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                                required
                                className="form-input"
                            />
                            <input
                                type="text"
                                placeholder="Phone (used to log in, e.g. 355000000)"
                                value={newDriver.phone}
                                onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                                required
                                className="form-input"
                            />
                            <input
                                type="text"
                                placeholder="WhatsApp number (e.g. 355000000)"
                                value={newDriver.whatsapp_number}
                                onChange={(e) => setNewDriver({ ...newDriver, whatsapp_number: e.target.value })}
                                required
                                className="form-input"
                            />
                            <input
                                type="password"
                                placeholder="Password"
                                value={newDriver.password}
                                onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })}
                                required
                                className="form-input"
                            />
                        </div>
                        <button type="submit" className="login-btn" style={{ width: 'auto', padding: '10px 24px' }}>
                            Create Driver
                        </button>
                    </form>
                )}

                <div className="bookings-list">
                    {drivers.length === 0 && <EmptyState icon={Users} label="No drivers yet" />}
                    {drivers.map((driver) => (
                        <div key={driver.id} className="booking-card">
                            {editingDriverId === driver.id ? (
                                <form onSubmit={(e) => handleUpdateDriver(e, driver.id)}>
                                    {driverError && <div className="error-message" style={{ marginBottom: '12px' }}>{driverError}</div>}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '12px' }}>
                                        <div>
                                            <label className="form-label">
                                                Driver Name
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. John Doe"
                                                value={editDriver.name}
                                                onChange={(e) => setEditDriver({ ...editDriver, name: e.target.value })}
                                                required
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">
                                                Login Phone Number (used to log into the driver dashboard)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. 355000000"
                                                value={editDriver.phone}
                                                onChange={(e) => setEditDriver({ ...editDriver, phone: e.target.value })}
                                                required
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">
                                                WhatsApp Number (receives ride notifications)
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. 355000000"
                                                value={editDriver.whatsapp_number}
                                                onChange={(e) => setEditDriver({ ...editDriver, whatsapp_number: e.target.value })}
                                                required
                                                className="form-input"
                                            />
                                        </div>
                                        <div>
                                            <label className="form-label">
                                                New Password
                                            </label>
                                            <input
                                                type="password"
                                                placeholder="Leave blank to keep current password"
                                                value={editDriver.password}
                                                onChange={(e) => setEditDriver({ ...editDriver, password: e.target.value })}
                                                className="form-input"
                                            />
                                        </div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={editDriver.active}
                                                onChange={(e) => setEditDriver({ ...editDriver, active: e.target.checked })}
                                            />
                                            <span>Active (unchecking disables their login and stops ride notifications)</span>
                                        </label>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button type="submit" className="login-btn" style={{ width: 'auto', padding: '10px 24px' }}>
                                            Save
                                        </button>
                                        <button
                                            type="button"
                                            className="logout-btn"
                                            onClick={() => setEditingDriverId(null)}
                                            style={{ width: 'auto', padding: '10px 24px' }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <>
                                    <div className="booking-header">
                                        <div className="booking-id">{driver.name}</div>
                                        <div className="booking-status" style={{ backgroundColor: driver.active ? 'var(--status-completed)' : 'var(--gray)' }}>
                                            {driver.active ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <div className="booking-details">
                                        <div className="detail-row">
                                            <span className="label">Phone:</span>
                                            <span className="value">{driver.phone}</span>
                                        </div>
                                    </div>
                                    <div className="booking-actions">
                                        <button className="login-btn" onClick={() => startEditDriver(driver)}>Edit</button>
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default AdminDrivers;